import {
	Anchor,
	Card,
	Center,
	Code,
	createStyles,
	Group,
	Stack,
} from '@mantine/core';
import type { BiMetricVersionOut } from '@repo/api-codegen';
import { Icon, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import type { ReactElement } from 'react';

import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { BiChartComponent } from './BiChartComponent.tsx';

interface BIMetricChartRenderProps {
	metricVersion: BiMetricVersionOut;
	mode: 'view_only' | 'full_edit';
	shadow?: boolean;
	isCreation?: boolean;
	onCreationSqlClick?: () => void;
	onCreationAiClick?: () => void;
}

// Development-friendly error fallback for chart rendering
function ChartErrorFallback({
	error,
	resetError,
}: {
	error: unknown;
	componentStack: string;
	eventId: string;
	resetError(): void;
}): ReactElement {
	const errorMessage = error instanceof Error ? error.message : String(error);
	const errorStack = error instanceof Error ? error.stack : undefined;
	const isDevelopment = import.meta.env.DEV;

	return (
		<Card withBorder radius="md" p="md">
			<Stack spacing="md">
				<Group>
					<Text size="lg" weight="semibold" color="text/critical/default">
						Chart Render Error
					</Text>
				</Group>

				<Text size="sm" color="text/secondary/default">
					An error occurred while rendering the chart.
				</Text>

				{isDevelopment && (
					<Stack spacing="xs">
						<Text size="sm" weight="semibold">
							Error Message:
						</Text>
						<Code block>{errorMessage}</Code>
					</Stack>
				)}

				{isDevelopment && errorStack && (
					<Stack spacing="xs">
						<Text size="sm" weight="semibold">
							Stack Trace:
						</Text>
						<Code block style={{ maxHeight: '200px', overflow: 'auto' }}>
							{errorStack}
						</Code>
					</Stack>
				)}
			</Stack>
		</Card>
	);
}

const useStyles = createStyles((theme, { shadow }: { shadow: boolean }) => ({
	frameContainer: {
		width: '100%',
	},
	chartCard: {
		backgroundColor: theme.other.getColor('surface/primary/default'),
		borderRadius: theme.other.borderRadius.md,
		boxShadow: shadow ? theme.shadows.sm : 'none',
		height: 240,
		[theme.fn.largerThan('md')]: {
			height: 280,
		},
		[theme.fn.largerThan('xl')]: {
			height: 320,
		},
		[theme.fn.largerThan(1800)]: {
			height: 360,
		},
		'& svg': {
			color: theme.other.getColor('border/secondary/default'),
			'& *': {
				color: 'initial',
			},
		},
	},
	emptyState: {
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	anchorTextEmptyState: {
		color: theme.other.getColor('text/emphasis/default'),
		fontSize: theme.other.typography.text.sm,
	},
}));

// Empty creation state for draft metrics that haven't been run yet
function EmptyCreationState({
	onCreationSqlClick,
	onCreationAiClick,
}: {
	onCreationSqlClick?: () => void;
	onCreationAiClick?: () => void;
}) {
	const { classes } = useStyles({ shadow: false });
	return (
		<Center h="100%">
			<Stack spacing="md" align="center" maw={400}>
				<Icon name="chartBar" size="lg" color="icon/secondary/default" />
				<Stack spacing="3xs" align="center">
					<Text size="md" weight="semibold" color="text/primary/default">
						Create metric chart
					</Text>
					<Text size="sm" color="text/secondary/default" ta="center">
						Get started by running a{' '}
						<Anchor
							size="sm"
							classNames={classes.anchorTextEmptyState}
							onClick={onCreationSqlClick}
						>
							SQL query
						</Anchor>{' '}
						or asking{' '}
						<Anchor
							size="sm"
							classNames={classes.anchorTextEmptyState}
							onClick={onCreationAiClick}
						>
							Secoda AI
						</Anchor>
					</Text>
				</Stack>
			</Stack>
		</Center>
	);
}

const BIMetricChartRenderInternal = observer(
	({
		metricVersion,
		mode,
		shadow = false,
		isCreation = false,
		onCreationSqlClick,
		onCreationAiClick,
	}: {
		metricVersion: BiMetricVersionOut;
		mode: BIMetricChartRenderProps['mode'];
		shadow?: boolean;
		isCreation?: boolean;
		onCreationSqlClick?: () => void;
		onCreationAiClick?: () => void;
	}) => {
		const { classes } = useStyles({
			shadow,
		});

		// Check if this is a creation metric without results
		const showEmptyCreationState =
			isCreation &&
			(!metricVersion.results || metricVersion.results.length === 0);

		return (
			<Card
				className={classes.chartCard}
				p={0}
				data-bi-metric-export-target={metricVersion.bi_metric_id}
			>
				{showEmptyCreationState ? (
					<EmptyCreationState
						onCreationSqlClick={onCreationSqlClick}
						onCreationAiClick={onCreationAiClick}
					/>
				) : (
					<BiChartComponent
						results={metricVersion.results}
						chartConfig={metricVersion.config.chart_config}
					/>
				)}
			</Card>
		);
	}
);

export function BiMetricChartRender(props: BIMetricChartRenderProps) {
	return (
		<ErrorBoundary fallback={ChartErrorFallback}>
			<BIMetricChartRenderInternal {...props} />
		</ErrorBoundary>
	);
}
